import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Home, Image, FileText, BookOpen, Info } from 'lucide-react'

export default function DocsPage() {
  useEffect(() => {
    // Enable scrolling (override global overflow:hidden)
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    
    // Handle hash scrolling on load
    if (window.location.hash) {
      const id = window.location.hash.substring(1)
      const element = document.getElementById(id)
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100)
      }
    }

    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [])

  const scrollToSection = (e, id) => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      window.history.pushState(null, '', `#${id}`)
    }
  }

  return (
    <div className="min-h-screen bg-ide-bg text-gray-100 overflow-auto font-sans" style={{ height: '100vh', overflowY: 'auto' }}>
      <Helmet>
        <title>P5 AI Studio User Guide | Documentation & Tutorials</title>
        <meta name="description" content="Learn how to use P5 AI Studio for creative coding. Guide to Vibe Coding, AI assistants, p5.js editor features, and publishing to the gallery." />
        <meta property="og:title" content="P5 AI Studio User Guide" />
        <meta property="og:description" content="Complete guide to AI-powered creative coding with P5 AI Studio." />
        <meta property="og:url" content="https://p5js.ai/docs" />
        <link rel="canonical" href="https://p5js.ai/docs" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-ide-border bg-ide-sidebar">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <BookOpen size={20} className="text-ide-accent" />
              User Guide
            </h1>
          </div>
          <nav className="flex items-center gap-4 text-sm hidden sm:flex">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
              <Home size={14} /> IDE
            </Link>
            <Link to="/gallery/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
              <Image size={14} /> Gallery
            </Link>
            <Link to="/about" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
              <Info size={14} /> About
            </Link>
            <Link to="/changelog" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
              <FileText size={14} /> Changelog
            </Link>
          </nav>
        </div>
      </header>

      <main id="main-content" className="max-w-[1200px] mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-[250px_1fr] gap-12">
        {/* Sidebar Navigation */}
        <aside className="hidden md:block sticky top-24 h-fit">
          <nav className="space-y-6">
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Getting Started</h3>
              <ul className="space-y-2 border-l border-gray-800">
                <li><a href="#introduction" onClick={(e) => scrollToSection(e, 'introduction')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Introduction</a></li>
                <li><a href="#quick-start" onClick={(e) => scrollToSection(e, 'quick-start')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Quick Start</a></li>
                <li><a href="#vibe-coding" onClick={(e) => scrollToSection(e, 'vibe-coding')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">What is Vibe Coding?</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">AI Features</h3>
              <ul className="space-y-2 border-l border-gray-800">
                <li><a href="#ai-assistant" onClick={(e) => scrollToSection(e, 'ai-assistant')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Using the Assistant</a></li>
                <li><a href="#models" onClick={(e) => scrollToSection(e, 'models')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Available Models</a></li>
                <li><a href="#chat-history" onClick={(e) => scrollToSection(e, 'chat-history')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Chat History</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">The Editor</h3>
              <ul className="space-y-2 border-l border-gray-800">
                <li><a href="#code-editor" onClick={(e) => scrollToSection(e, 'code-editor')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Code Editor</a></li>
                <li><a href="#preview" onClick={(e) => scrollToSection(e, 'preview')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Live Preview</a></li>
                <li><a href="#console" onClick={(e) => scrollToSection(e, 'console')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Debug Console</a></li>
                <li><a href="#files" onClick={(e) => scrollToSection(e, 'files')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Multi-File Projects</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Sharing</h3>
              <ul className="space-y-2 border-l border-gray-800">
                <li><a href="#gallery" onClick={(e) => scrollToSection(e, 'gallery')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Publishing to Gallery</a></li>
                <li><a href="#download" onClick={(e) => scrollToSection(e, 'download')} className="block pl-4 text-gray-400 hover:text-ide-accent hover:border-l-2 hover:border-ide-accent -ml-[1px] transition-colors text-sm">Downloading & Syncing</a></li>
              </ul>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <article className="prose prose-invert max-w-none prose-h1:text-4xl prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-gray-800 prose-h2:pb-4 prose-p:text-gray-300 prose-p:leading-relaxed prose-a:text-ide-accent prose-li:text-gray-300">
          <header className="mb-12 border-b border-gray-800 pb-8">
            <h1 className="mb-4">P5 AI Studio User Guide</h1>
            <p className="text-xl text-gray-400">Everything you need to know to create generative art with AI.</p>
          </header>

          <section id="introduction">
            <h2>Introduction</h2>
            <p>
              P5 AI Studio is a specialized development environment for <strong>creative coding with p5.js</strong>. 
              Unlike standard text editors, it integrates powerful AI models directly into your workflow, allowing you 
              to write code by describing what you want to see—a process we call "Vibe Coding."
            </p>
            <p>
              Whether you're a seasoned creative technologist or a complete beginner, this tool is designed to 
              accelerate your experimentation and help you visualize ideas instantly.
            </p>
          </section>

          <section id="quick-start">
            <h2>Quick Start</h2>
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Open the Editor:</strong> Go to <Link to="/">p5js.ai</Link>. No account is needed to start.
              </li>
              <li>
                <strong>Ask the AI:</strong> In the right-hand panel, type a prompt like:
                <em>"Create a particle system that reacts to music"</em> or <em>"Draw a recursive tree that sways in the wind."</em>
              </li>
              <li>
                <strong>Run & Iterate:</strong> The AI will generate code. Click "Run" to see it in the Preview panel. 
                If it's not quite right, tell the AI what to change: <em>"Make the particles blue"</em> or <em>"Slow down the animation."</em>
              </li>
            </ol>
          </section>

          <section id="vibe-coding">
            <h2>What is Vibe Coding?</h2>
            <p>
              <strong>Vibe Coding</strong> is a new way of programming where you focus on the <em>feeling</em>, <em>behavior</em>, 
              and <em>outcome</em> of your software rather than the syntax.
            </p>
            <p>
              Instead of worrying about semicolons or memorizing math formulas, you describe the "vibe"—the aesthetic and 
              interactive qualities you want. The AI acts as your technical partner, translating your vision into working JavaScript.
            </p>
            <div className="my-6 p-6 bg-pink-500/10 border-l-4 border-ide-accent rounded-r-lg">
              <strong className="text-ide-accent block mb-2">Tip:</strong> 
              Be descriptive! "Make it look cool" is vague. "Make it look like a 1980s retro synthwave grid with neon colors" gives the AI much more to work with.
            </div>
          </section>

          <section id="ai-assistant">
            <h2>Using the AI Assistant</h2>
            <p>
              The AI panel is your copilot. It can:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Write Code:</strong> Generate full sketches from scratch.</li>
              <li><strong>Edit Code:</strong> "Change the background to black" or "Add mouse interaction."</li>
              <li><strong>Fix Bugs:</strong> If your code crashes, paste the error or ask "Why isn't this working?"</li>
              <li><strong>Explain:</strong> "Explain how the Perlin noise loop works."</li>
            </ul>
          </section>

          <section id="models">
            <h2>Available Models</h2>
            <p>P5 AI Studio offers several models optimized for different tasks:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>GPT-5.1 Mini (OpenAI):</strong> Fast, smart, and great for general logic. The default choice.</li>
              <li><strong>Claude 4.5 Sonnet (Anthropic):</strong> Excellent at creative writing and explaining complex concepts.</li>
              <li><strong>Gemini 2.5 Flash (Google):</strong> Extremely fast and handles large context well.</li>
              <li><strong>p5flash (Custom):</strong> Our own fine-tuned model trained specifically on p5.js examples. Best for p5-specific syntax.</li>
            </ul>
          </section>

          <section id="chat-history">
            <h2>Chat History</h2>
            <p>
              Every conversation is saved locally in your browser. Click the <span className="inline-block bg-gray-800 px-1 rounded">🕒</span> History icon 
              in the header to jump back to previous sessions. You can also view the specific AI prompts used to generate 
              any sketch in the <Link to="/gallery/">Gallery</Link>.
            </p>
          </section>

          <section id="code-editor">
            <h2>Code Editor</h2>
            <p>
              The central panel is a full-featured Monaco editor (same as VS Code). It supports:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Syntax Highlighting:</strong> For JavaScript, HTML, CSS, and JSON.</li>
              <li><strong>Autocomplete:</strong> Smart suggestions for p5.js functions.</li>
              <li><strong>Inline Errors:</strong> Red squiggles for syntax errors.</li>
              <li><strong>Command Palette:</strong> Press <kbd className="bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700 text-sm">Ctrl</kbd>+<kbd className="bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700 text-sm">K</kbd> (or Cmd+K) to access quick actions.</li>
            </ul>
          </section>

          <section id="preview">
            <h2>Live Preview</h2>
            <p>
              The Preview panel runs your code in real-time. It uses a secure sandbox that allows access to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Webcam & Microphone (audio-reactive visuals)</li>
              <li>Mouse & Keyboard input</li>
              <li>Window resizing events</li>
            </ul>
            <p>
              Click the <span className="inline-block bg-gray-800 px-1 rounded">↗</span> Pop-out button to open your sketch in a full standalone window—great for second monitors or projectors.
            </p>
          </section>

          <section id="gallery">
            <h2>Publishing to Gallery</h2>
            <p>
              Proud of your creation? Share it with the world!
            </p>
            <ol className="list-decimal pl-6 space-y-4">
              <li>Click the <strong>Publish</strong> button in the header.</li>
              <li>Give your sketch a title.</li>
              <li>(Optional) Click "AI Write" to have the AI generate a description for you.</li>
              <li>Choose whether to make it <strong>Public</strong> (visible to everyone) or <strong>Private</strong> (link only).</li>
              <li>Click Publish. You'll get a permanent URL like <code>p5js.ai/gallery/s/your-sketch-slug</code>.</li>
            </ol>
            <p>
              Check out the <Link to="/gallery/">Gallery</Link> to see what others are making and "Remix" their code!
            </p>
          </section>
        </article>
      </main>

      <footer className="border-t border-ide-border mt-12 py-8 text-center text-gray-500 text-sm bg-ide-sidebar">
        <p>P5 AI Studio — Making creative coding accessible through AI</p>
        <div className="flex justify-center gap-4 mt-4">
          <Link to="/" className="hover:text-white transition-colors">Editor</Link>
          <Link to="/gallery/" className="hover:text-white transition-colors">Gallery</Link>
          <Link to="/changelog" className="hover:text-white transition-colors">Changelog</Link>
          <a href="mailto:ai@digitalpocket.com" className="hover:text-white transition-colors">Contact</a>
        </div>
      </footer>
    </div>
  )
}
